import React from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@mui/material';
import { useIsMobile } from '../common/layout_hooks';
import MealDetailReadOnlyMobile from './meal_detail_read_only_mobile';
import MealDetailReadOnlyDesktop from './meal_detail_read_only_desktop';
import MealDetailEditableDesktop from './meal_detail_editable_desktop';
import MealDetailEditableMobile from './meal_detail_editable_mobile';
import { userIsCreator } from '../../services/users';
import { useGetUserProfileQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import MealDetailLoadingMobile from './meal_detail_loading_mobile';
import MealDetailLoadingDesktop from './meal_detail_loading_desktop';

const MealDetailModal = ({
  fullMeal,
  isLoading,
  menu,
  isReadOnly,
  alwaysShowNutrition,
  savingInProgress,
  showNutritionMetrics,
  derivedNutrition,
  canScaleIngredients,
  scaleToServings,
  currentlyEditingInline,
  ingredientSuggestions,
  ingredientSuggestionsNetworkState,
  onInlineEditFocus,
  onInlineEditChange,
  onInlineEditBlur,
  onAddArraySectionItem,
  onRemoveArraySectionItem,
  onIngredientCheckChange,
  onEditScaleToServings,
  onChangeScaleToServings,
  resetScaleToServings,
  onNewImageUrlSet,
  onNewImageChosen,
  onRuleChanged,
  onAddonChange,
  onTagsChange,
  onDialogClose,
  onUpdateIngredientStructuredData,
}) => {
  const isMobile = useIsMobile();
  // console.log(
  //   `MealDetailModal isLoading=${isLoading} fullMealIsPresent=${!!fullMeal}`,
  // );

  const { data: userProfile } = useGetUserProfileQuery();

  const isSmorgStudio = userIsCreator(userProfile);

  return (
    <Dialog
      open
      maxWidth="md"
      // Prevents TinyMCE issue - disabled inputs in Image dialogs invoked from toolbar
      disableEnforceFocus
      fullWidth
      onClose={onDialogClose}>
      {isMobile && isLoading && <MealDetailLoadingMobile />}
      {isMobile && !isLoading && isReadOnly && (
        <MealDetailReadOnlyMobile
          menu={menu}
          meal={fullMeal}
          savingInProgress={savingInProgress}
          showNutritionMetrics={showNutritionMetrics}
          alwaysShowNutrition={alwaysShowNutrition}
          showMacroRatio
          derivedNutrition={derivedNutrition}
          canScaleIngredients={canScaleIngredients}
          scaleToServings={scaleToServings}
          onIngredientCheckChange={onIngredientCheckChange}
          onEditScaleToServings={onEditScaleToServings}
          onChangeScaleToServings={onChangeScaleToServings}
          resetScaleToServings={resetScaleToServings}
          onDialogClose={onDialogClose}
        />
      )}
      {isMobile && !isLoading && !isReadOnly && (
        <MealDetailEditableMobile
          menu={menu}
          meal={fullMeal}
          savingInProgress={savingInProgress}
          showNutritionMetrics={showNutritionMetrics}
          alwaysShowNutrition={alwaysShowNutrition}
          showMacroRatio
          derivedNutrition={derivedNutrition}
          canScaleIngredients={canScaleIngredients}
          scaleToServings={scaleToServings}
          currentlyEditingInline={currentlyEditingInline}
          ingredientSuggestions={ingredientSuggestions}
          ingredientSuggestionsNetworkState={ingredientSuggestionsNetworkState}
          onInlineEditFocus={onInlineEditFocus}
          onInlineEditChange={onInlineEditChange}
          onInlineEditBlur={onInlineEditBlur}
          onAddArraySectionItem={onAddArraySectionItem}
          onRemoveArraySectionItem={onRemoveArraySectionItem}
          onIngredientCheckChange={onIngredientCheckChange}
          onEditScaleToServings={onEditScaleToServings}
          onChangeScaleToServings={onChangeScaleToServings}
          resetScaleToServings={resetScaleToServings}
          onImageGeneratedAI={onNewImageUrlSet}
          onNewImageChosen={onNewImageChosen}
          onAddonChange={onAddonChange}
          onTagsChange={onTagsChange}
          onDialogClose={onDialogClose}
          onUpdateIngredientStructuredData={onUpdateIngredientStructuredData}
        />
      )}
      {!isMobile && isLoading && <MealDetailLoadingDesktop />}
      {!isMobile && !isLoading && isReadOnly && (
        <MealDetailReadOnlyDesktop
          menu={menu}
          meal={fullMeal}
          showNutritionMetrics={showNutritionMetrics}
          alwaysShowNutrition={alwaysShowNutrition}
          showMacroRatio
          derivedNutrition={derivedNutrition}
          canScaleIngredients={canScaleIngredients}
          scaleToServings={scaleToServings}
          onDialogClose={onDialogClose}
        />
      )}
      {!isMobile && !isLoading && !isReadOnly && (
        <MealDetailEditableDesktop
          menu={menu}
          meal={fullMeal}
          richTextDescription={!isReadOnly && isSmorgStudio && !isMobile}
          savingInProgress={savingInProgress}
          showNutritionMetrics={showNutritionMetrics}
          alwaysShowNutrition={alwaysShowNutrition}
          showMacroRatio
          derivedNutrition={derivedNutrition}
          canScaleIngredients={canScaleIngredients}
          scaleToServings={scaleToServings}
          currentlyEditingInline={currentlyEditingInline}
          ingredientSuggestions={ingredientSuggestions}
          ingredientSuggestionsNetworkState={ingredientSuggestionsNetworkState}
          onInlineEditFocus={onInlineEditFocus}
          onInlineEditChange={onInlineEditChange}
          onInlineEditBlur={onInlineEditBlur}
          onAddArraySectionItem={onAddArraySectionItem}
          onRemoveArraySectionItem={onRemoveArraySectionItem}
          onEditScaleToServings={onEditScaleToServings}
          onChangeScaleToServings={onChangeScaleToServings}
          resetScaleToServings={resetScaleToServings}
          onImageGeneratedAI={onNewImageUrlSet}
          onNewImageChosen={onNewImageChosen}
          onRuleChanged={onRuleChanged}
          onAddonChange={onAddonChange}
          onTagsChange={onTagsChange}
          onDialogClose={onDialogClose}
          onUpdateIngredientStructuredData={onUpdateIngredientStructuredData}
        />
      )}
    </Dialog>
  );
};

MealDetailModal.propTypes = {
  menu: PropTypes.shape({
    title: PropTypes.string.isRequired,
  }).isRequired,
  fullMeal: PropTypes.shape({
    id: PropTypes.string.isRequired,
    recipes: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        shortDescription: PropTypes.string,
        imageUrl: PropTypes.string,
        recipeUrl: PropTypes.string,
        mealiqId: PropTypes.string,
        mealTypes: PropTypes.arrayOf(PropTypes.string),
        nutrition: PropTypes.shape({
          kCal: PropTypes.number,
          protein: PropTypes.number,
          fat: PropTypes.number,
          carbohydrate: PropTypes.number,
          fibre: PropTypes.number,
          sugar: PropTypes.number,
        }),
        supportedDiets: PropTypes.arrayOf(PropTypes.string),
        ingredients: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            fullText: PropTypes.string,
          }),
        ),
        preparations: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            fullText: PropTypes.string,
          }),
        ),
        utensils: PropTypes.arrayOf(PropTypes.string).isRequired,
        servings: PropTypes.number,
        cookTime: PropTypes.number,
        prepTime: PropTypes.number,
        totalTime: PropTypes.number,
      }),
    ).isRequired,
    addons: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool,
  alwaysShowNutrition: PropTypes.bool,
  savingInProgress: PropTypes.bool.isRequired,
  derivedNutrition: PropTypes.shape({
    totalNutritionPerServing: PropTypes.objectOf(PropTypes.number),
    // eslint-disable-next-line react/forbid-prop-types
    ingredientNutrition: PropTypes.arrayOf(PropTypes.object).isRequired,
    macroRatio: PropTypes.objectOf(PropTypes.number),
    foodAllergens: PropTypes.arrayOf(PropTypes.string).isRequired,
    foodPreferences: PropTypes.arrayOf(PropTypes.string).isRequired,
    status: PropTypes.bool.isRequired,
  }).isRequired,
  showNutritionMetrics: PropTypes.arrayOf(PropTypes.string).isRequired,
  canScaleIngredients: PropTypes.bool.isRequired,
  scaleToServings: PropTypes.number.isRequired,
  currentlyEditingInline: PropTypes.shape({
    section: PropTypes.string.isRequired,
    itemId: PropTypes.string,
  }).isRequired,
  ingredientSuggestions: PropTypes.arrayOf(
    PropTypes.shape({
      suggestionText: PropTypes.string.isRequired,
    }),
  ).isRequired,
  ingredientSuggestionsNetworkState: PropTypes.shape({
    loading: PropTypes.bool,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  onInlineEditFocus: PropTypes.func.isRequired,
  onInlineEditChange: PropTypes.func.isRequired,
  onInlineEditBlur: PropTypes.func.isRequired,
  onAddArraySectionItem: PropTypes.func.isRequired,
  onRemoveArraySectionItem: PropTypes.func.isRequired,
  onIngredientCheckChange: PropTypes.func.isRequired,
  onEditScaleToServings: PropTypes.func.isRequired,
  onChangeScaleToServings: PropTypes.func.isRequired,
  resetScaleToServings: PropTypes.func.isRequired,
  onNewImageUrlSet: PropTypes.func.isRequired,
  onNewImageChosen: PropTypes.func.isRequired,
  onRuleChanged: PropTypes.func.isRequired,
  onAddonChange: PropTypes.func.isRequired,
  onTagsChange: PropTypes.func.isRequired,
  onDialogClose: PropTypes.func.isRequired,
  onUpdateIngredientStructuredData: PropTypes.func.isRequired,
};

MealDetailModal.defaultProps = {
  isReadOnly: false,
  alwaysShowNutrition: false,
};

export default MealDetailModal;
